/**
 * Model presets and capability table.
 *
 * All presets use WebLLM (@mlc-ai/web-llm) and run 100% locally in the browser.
 * Weights are downloaded once from the MLC/HuggingFace CDN (or your own mirror via
 * `modelUrl`) and cached offline — no cloud API calls during inference.
 */

/** Custom ModelRecord fields for presets not in WebLLM's prebuiltAppConfig. */
export interface CustomModelRecord {
  model: string;
  model_id: string;
  model_lib: string;
  vram_required_MB?: number;
  low_resource_required?: boolean;
  required_features?: string[];
  overrides?: { context_window_size?: number };
}

export interface Preset {
  name: string;
  modelId: string;
  /** Approximate HuggingFace weight download size (MB). */
  downloadMb: number;
  /** Approximate WebLLM VRAM requirement (MB). */
  vramMb: number;
  toolSupport: 'native' | 'manual' | 'none' | 'unverified';
  recommended: string;
  /** Present when the model is not in WebLLM prebuiltAppConfig. */
  customRecord?: CustomModelRecord;
}

/** WebLLM model library version compatible with current @mlc-ai/web-llm. */
export const MLC_MODEL_VERSION = 'v0_2_84/base';
export const MLC_MODEL_LIB_PREFIX =
  'https://raw.githubusercontent.com/mlc-ai/binary-mlc-llm-libs/main/web-llm-models/';

/** Fallback if coign-quick custom record fails to load. */
export const QUICK_FALLBACK_MODEL_ID = 'SmolLM2-360M-Instruct-q4f16_1-MLC';

export const PRESETS: Record<string, Preset> = {
  'coign-quick': {
    name: 'coign-quick',
    modelId: 'SmolLM2-135M-Instruct-q4f16_1-MLC',
    downloadMb: 79,
    vramMb: 200,
    toolSupport: 'none',
    recommended: 'First-answer progressive path (~79 MB)',
    customRecord: {
      model: 'https://huggingface.co/mlc-ai/SmolLM2-135M-Instruct-q4f16_1-MLC',
      model_id: 'SmolLM2-135M-Instruct-q4f16_1-MLC',
      model_lib:
        MLC_MODEL_LIB_PREFIX +
        MLC_MODEL_VERSION +
        '/SmolLM2-135M-Instruct-q4f16_1_cs1k-webgpu.wasm',
      vram_required_MB: 200,
      low_resource_required: true,
      required_features: ['shader-f16'],
      overrides: { context_window_size: 2048 },
    },
  },
  'coign-tiny': {
    name: 'coign-tiny',
    modelId: 'SmolLM2-360M-Instruct-q4f16_1-MLC',
    downloadMb: 207,
    vramMb: 376,
    toolSupport: 'none',
    recommended: 'Smaller devices; quick fallback',
  },
  'coign-lite': {
    name: 'coign-lite',
    modelId: 'Llama-3.2-1B-Instruct-q4f16_1-MLC',
    downloadMb: 700,
    vramMb: 1120,
    toolSupport: 'manual',
    recommended: 'Default full model — docs, FAQ pages',
  },
  'coign-balanced': {
    name: 'coign-balanced',
    modelId: 'Llama-3.2-3B-Instruct-q4f16_1-MLC',
    downloadMb: 1800,
    vramMb: 2260,
    toolSupport: 'manual',
    recommended: 'Stronger reasoning with tools',
  },
  'coign-quality': {
    name: 'coign-quality',
    modelId: 'Llama-3.1-8B-Instruct-q4f16_1-MLC',
    downloadMb: 4500,
    vramMb: 5100,
    toolSupport: 'manual',
    recommended: 'Long-form reasoning, complex questions',
  },
  'coign-tools': {
    name: 'coign-tools',
    modelId: 'Hermes-2-Pro-Llama-3-8B-q4f16_1-MLC',
    downloadMb: 4500,
    vramMb: 5100,
    toolSupport: 'native',
    recommended: 'When tool-call reliability matters most',
  },
};

export function resolvePreset(input: string): string {
  const preset = PRESETS[input];
  return preset ? preset.modelId : input;
}

export function getPreset(input: string): Preset | undefined {
  return PRESETS[input];
}

export function getPresetByModelId(modelId: string): Preset | undefined {
  for (const key in PRESETS) {
    if (PRESETS[key].modelId === modelId) return PRESETS[key];
  }
  return undefined;
}

export function getCustomModelRecord(input: string): CustomModelRecord | undefined {
  const preset = PRESETS[input] ?? getPresetByModelId(input);
  return preset?.customRecord;
}

export function getPresetSupport(modelId: string): 'native' | 'manual' | 'none' {
  const preset = getPresetByModelId(modelId) ?? PRESETS[modelId];
  if (!preset) return 'manual';
  if (preset.toolSupport === 'native') return 'native';
  if (preset.toolSupport === 'none') return 'none';
  return 'manual';
}

/** Legacy aliases mapped onto the slim preset set. */
const ALIASES: Record<string, string> = {
  'coign-local': 'coign-balanced',
  'coign-creative': 'coign-quality',
  'coign-code': 'coign-tools',
};

export function resolvePresetName(input: string): string {
  return ALIASES[input] ?? input;
}
